<script setup>
import { nextTick, onUnmounted, ref, watch } from 'vue'

// Ngăn trượt bên phải: dùng cho xem chi tiết / tạo / sửa mà không chiếm chỗ cố định trên trang.
const props = defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, required: true },
  eyebrow: { type: String, default: '' },
  wide: { type: Boolean, default: false },
})
const emit = defineEmits(['close'])
const panel = ref(null)

function onKey(event) {
  if (event.key === 'Escape') emit('close')
}

watch(() => props.open, async open => {
  document.body.style.overflow = open ? 'hidden' : ''
  if (open) {
    window.addEventListener('keydown', onKey)
    await nextTick()
    panel.value?.focus()
  } else {
    window.removeEventListener('keydown', onKey)
  }
}, { immediate: true })

onUnmounted(() => {
  document.body.style.overflow = ''
  window.removeEventListener('keydown', onKey)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="drawer">
      <div v-if="open" class="drawer-root">
        <button type="button" class="drawer-overlay" aria-label="Đóng" @click="emit('close')"></button>
        <aside ref="panel" class="drawer-panel" :class="{ wide }" role="dialog" aria-modal="true" :aria-label="title" tabindex="-1">
          <header class="drawer-head">
            <div><p v-if="eyebrow" class="eyebrow">{{ eyebrow }}</p><h2>{{ title }}</h2><slot name="subtitle" /></div>
            <div class="drawer-head-actions"><slot name="actions" /><button type="button" class="drawer-close" aria-label="Đóng" @click="emit('close')">×</button></div>
          </header>
          <div class="drawer-body"><slot /></div>
          <footer v-if="$slots.footer" class="drawer-foot"><slot name="footer" /></footer>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.drawer-root { position: fixed; inset: 0; z-index: 60; }
.drawer-overlay { position: absolute; inset: 0; border: 0; background: rgba(24, 25, 64, .32); cursor: pointer; }
.drawer-panel { position: absolute; top: 0; right: 0; bottom: 0; width: min(460px, 100vw); display: flex; flex-direction: column; background: #fff; box-shadow: -20px 0 60px rgba(24, 25, 64, .18); outline: none; }
.drawer-panel.wide { width: min(600px, 100vw); }
.drawer-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; padding: 22px 24px 16px; border-bottom: 1px solid var(--border); }
.drawer-head h2 { margin: 0; font-size: 21px; line-height: 1.3; }
.drawer-head-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.drawer-close { width: 36px; height: 36px; border: 0; border-radius: 10px; background: #f3f2f8; color: #6b7090; font-size: 22px; line-height: 1; cursor: pointer; }
.drawer-close:hover { background: #e9e7f5; }
.drawer-body { flex: 1; overflow: auto; padding: 20px 24px; }
.drawer-foot { display: flex; gap: 10px; padding: 14px 24px; border-top: 1px solid var(--border); background: #fcfcff; }
.drawer-enter-active, .drawer-leave-active { transition: opacity .2s ease; }
.drawer-enter-active .drawer-panel, .drawer-leave-active .drawer-panel { transition: transform .22s ease; }
.drawer-enter-from, .drawer-leave-to { opacity: 0; }
.drawer-enter-from .drawer-panel, .drawer-leave-to .drawer-panel { transform: translateX(40px); }
@media (max-width: 560px) {
  .drawer-head { padding: 18px 16px 14px; }
  .drawer-body { padding: 16px; }
  .drawer-foot { padding: 12px 16px; }
}
</style>
